<template>
  <div>
    <button
      class="toggle-salary-details-btn"
      :class="{ 'is-active': isSalaryDetailsExpanded }"
      @click="toggleSalaryDetails"
      :aria-controls="'salary-accordion-' + id"
      :aria-expanded="isSalaryDetailsExpanded"
    >
      {{ salaryDetailsButtonText }}
      <div class="circle" aria-hidden="true">
        <svg>
          <use :href="`${spritemap}#triangle`"></use>
        </svg>
      </div>
    </button>
    <div
      ref="accordion"
      class="accordion-panel"
      :id="'salary-accordion-' + id"
      :class="{ active: isSalaryDetailsExpanded }"
    >
      <table class="salary-details" :class="{ secondary: secondary }">
        <tbody>
          <tr>
            <td class="info">{{ d['Salary.Result.LabelBaseSalary']?.item2 }}</td>
            <td class="amount">{{ baseSalary }}</td>
          </tr>
          <tr>
            <td class="info">{{ d['Salary.Result.LabelPension']?.item2 }}</td>
            <td class="amount">{{ pension }}</td>
          </tr>
          <tr>
            <td class="info">{{ d['Salary.Result.LabelIrregularPayments']?.item2 }}</td>
            <td class="amount">{{ irregularPayments }}</td>
          </tr>
          <tr>
            <td class="info">{{ d['Salary.Result.LabelHolidayAllowance']?.item2 }}</td>
            <td class="amount">{{ holidayAllowance }}</td>
          </tr>
          <tr>
            <td></td>
            <td class="amount">{{ salaryTotal }}</td>
          </tr>
        </tbody>
      </table>
      <p class="salary-details__bottom-text text-small">
        {{ d['Salary.Result.SalaryDetailsBottomText']?.item2 }}
      </p>
    </div>
  </div>
</template>
<script>
import { state, localDictionary } from '@/lib/state'
import { svgSpritemapSrc } from '@/lib/mapping'

export default {
  name: 'SalaryAccordion',
  props: {
    secondary: {
      type: Boolean,
      default: false,
    },
    quartile: {
      type: String,
      default: 'q2', // fallback to median
    },
  },
  data() {
    return {
      isSalaryDetailsExpanded: false,
      d: localDictionary(),
      spritemap: svgSpritemapSrc,
    }
  },
  computed: {
    id() {
      return Math.random().toString(36).substr(2, 9)
    },
    currentQ() {
      // return `q${state.salarySliderIndex + 1}` // TODO: fix if/when values get prefixed with q1 etc
      return ''
    },
    labelCurrency() {
      return this.d['Salary.Result.LabelCurrency']?.item2 || ''
    },
    salaryTotal() {
      const key = this.quartile
      return this.getResultText(key)
    },
    baseSalary() {
      const key = this.quartile === 'q2' ? 'baseSalary' : `baseSalary${this.quartile}`
      return this.getResultText(key)
    },
    pension() {
      const key = this.quartile === 'q2' ? 'pension' : `pension${this.quartile}`
      return this.getResultText(key)
    },
    irregularPayments() {
      const key = this.quartile === 'q2' ? 'irregularPayments' : `irregularPayments${this.quartile}`
      return this.getResultText(key)
    },
    holidayAllowance() {
      const key = this.quartile === 'q2' ? 'holidayAllowance' : `holidayAllowance${this.quartile}`
      return this.getResultText(key)
    },

    salaryDetailsButtonText() {
      return this.isSalaryDetailsExpanded
        ? this.d['Salary.Result.SalaryDetailsHide']?.item2
        : this.d['Salary.Result.SalaryDetailsShow']?.item2
    },
  },
  methods: {
    toggleSalaryDetails() {
      this.isSalaryDetailsExpanded = !this.isSalaryDetailsExpanded
      this.$nextTick(() => {
        const accordion = this.$refs.accordion
        accordion.addEventListener('transitionend', () => {
          if (!this.isSalaryDetailsExpanded) content.style.display = 'none'
        })
        const content = accordion.querySelector('.salary-details')
        if (this.isSalaryDetailsExpanded) {
          content.style.display = 'table'
          accordion.style.maxHeight = accordion.scrollHeight + 'px'
        } else {
          accordion.style.maxHeight = '0'
        }
      })
    },
    getResultText(postfix = '') {
      const qKey = this.currentQ + postfix
      const value = state.resultData?.[qKey]

      if (value == null) return ` ${this.labelCurrency}`

      const formatted = new Intl.NumberFormat(state.culture).format(value)
      return `${formatted} ${this.labelCurrency}`
    },
  },
}
</script>
<style lang="postcss" scoped>
.toggle-salary-details-btn {
  font-family: var(--font-it-ex);
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-xs);
  line-height: 2;
  letter-spacing: 0.24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: var(--grid-gutter-mobile) auto 0 auto;

  @media (min-width: 1024px) {
    display: none;
  }

  &.is-active svg {
    transform: rotate(180deg);
  }

  .circle {
    margin-top: 4px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background-color: var(--color-white);
  }

  svg {
    color: var(--color-blue);
    width: 12px;
    height: 9px;
    transition: transform var(--ani-speed-3) var(--cubic-ease-in-out);
  }
}

.accordion-panel {
  max-height: 0;
  overflow: hidden;
  transition: max-height var(--ani-speed-3) var(--cubic-ease-in-out);

  @media (min-width: 1024px) {
    max-height: initial;
    overflow: visible;
  }

  .salary-details {
    margin-top: 12px;
    display: none;
    border-collapse: collapse;
    text-align: left;
    width: 100%;
    height: 0;
    font-family: var(--font-it-ex);
    
    .info {
      font-weight: 400;
      font-size: 14px;
    }

    &.secondary tr {
      border-color: #4084f7;

      .info {
        font-weight: 400;
        font-size: 14px;
      }
      .amount {
        font-weight: 400;
        font-size: 18px;
      }

    @media (min-width: 1024px) {
       .info {
        margin-top: 16px;
       }
    }
    }

    @media (min-width: 1024px) {
      margin-top: 20px;
    }

    &__bottom-text {
      margin-top: 18px;
      text-align: left;
    }

    tr {
      border-top: 1px solid #4084f7;

      td {
        padding: 8px 0;

        &:first-child {
          padding-right: 16px;
        }

        &:last-child {
          min-width: 80px;
          text-align: right;
        }
      }

      .info {
        font-weight: 400;
        font-size: 16px;
      }
      .amount {
        font-weight: 400;
        font-size: 18px;
      }
    }

    @media (min-width: 1024px) {
      display: table;
    }
  }
}
</style>
